const ExcelJS = require("exceljs");
const JSZip = require("jszip");
const { saveAs } = require("file-saver");
const {
  PRESERVICE_STATUS_SHEET_NAME,
  PRESERVICE_STATUS_TEMPLATE_HEADERS,
  PRESERVICE_TEMPLATE_VERSION,
  getRequiredPreserviceStudentHeaders,
} = require("./preservice-import-contract");

const TEMPLATE_PASSWORD = "HRD-Preservice-Template";
const TEMPLATE_LAST_ROW = 201;
const PROGRAM_FAN_RANGE_NAME = "PreserviceProgramFans";
const formatAllowedValues = (values = []) =>
  values.map((value) => String(value || "").trim()).filter(Boolean).join(", ");

const addWorkbookStructureProtection = async (workbookBuffer) => {
  const zip = await JSZip.loadAsync(workbookBuffer);
  const workbookXmlFile = zip.file("xl/workbook.xml");
  if (!workbookXmlFile) {
    return workbookBuffer;
  }

  let workbookXml = await workbookXmlFile.async("string");
  if (!workbookXml.includes("<workbookProtection")) {
    const protectionXml = '<workbookProtection lockStructure="1"/>';
    if (/<workbookPr\b[^>]*\/>/.test(workbookXml)) {
      workbookXml = workbookXml.replace(
        /(<workbookPr\b[^>]*\/>)/,
        `$1${protectionXml}`,
      );
    } else {
      workbookXml = workbookXml.replace(
        /(<workbook\b[^>]*>)/,
        `$1${protectionXml}`,
      );
    }
    zip.file("xl/workbook.xml", workbookXml);
  }

  return zip.generateAsync({ type: "arraybuffer" });
};

export const getColumnLetter = (index) => {
  let dividend = index;
  let columnName = "";
  while (dividend > 0) {
    const modulo = (dividend - 1) % 26;
    columnName = String.fromCharCode(65 + modulo) + columnName;
    dividend = Math.floor((dividend - modulo) / 26);
  }
  return columnName;
};

const setListValidation = ({
  columnMap,
  error,
  errorTitle,
  header,
  listFormulaByHeader,
  rowNumber,
  templateSheet,
}) => {
  if (!columnMap[header] || !listFormulaByHeader[header]) {
    return;
  }

  templateSheet.getCell(`${columnMap[header]}${rowNumber}`).dataValidation = {
    type: "list",
    allowBlank: false,
    formulae: [listFormulaByHeader[header]],
    showErrorMessage: true,
    errorTitle,
    error,
  };
};

const setYearValidation = ({
  allowBlank,
  columnHeader,
  columnMap,
  error,
  errorTitle,
  maximumYear = 9999,
  rowNumber,
  templateSheet,
}) => {
  if (!columnMap[columnHeader]) {
    return;
  }

  templateSheet.getCell(
    `${columnMap[columnHeader]}${rowNumber}`,
  ).dataValidation = {
    type: "whole",
    operator: "between",
    allowBlank,
    formulae: [1950, maximumYear],
    showErrorMessage: true,
    errorTitle,
    error,
  };
};

const setGpaValidation = ({
  allowBlank,
  columnHeader,
  columnMap,
  error,
  rowNumber,
  templateSheet,
}) => {
  if (!columnMap[columnHeader]) {
    return;
  }

  templateSheet.getCell(
    `${columnMap[columnHeader]}${rowNumber}`,
  ).dataValidation = {
    type: "decimal",
    operator: "between",
    allowBlank,
    formulae: [0, 4],
    showErrorMessage: true,
    errorTitle: "Invalid GPA",
    error,
  };
};

const setRequiredTextValidation = ({
  columnHeader,
  columnMap,
  error,
  rowNumber,
  templateSheet,
}) => {
  if (!columnMap[columnHeader]) {
    return;
  }

  templateSheet.getCell(
    `${columnMap[columnHeader]}${rowNumber}`,
  ).dataValidation = {
    type: "textLength",
    operator: "greaterThan",
    allowBlank: false,
    formulae: [0],
    showErrorMessage: true,
    errorTitle: "Required field",
    error: error || `${columnHeader} is required.`,
  };
};

const styleRequiredHeaders = ({ headers, requiredHeaders, sheet }) => {
  const requiredSet = new Set(requiredHeaders);
  headers.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: requiredSet.has(header) ? "FFC62828" : "FF455A64",
      },
    };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.note = requiredSet.has(header)
      ? `${header} is required.`
      : `${header} is optional.`;
  });
};

const uniqueStrings = (values = []) => [
  ...new Set(values.map((value) => String(value || "").trim()).filter(Boolean)),
];

const addDependentListMap = ({
  groups,
  lookupSheet,
  workbook,
  keyHeader,
  namePrefix,
  startColumn,
}) => {
  const mappingColumn = getColumnLetter(startColumn);
  const rangeNameColumn = getColumnLetter(startColumn + 1);
  const valuesColumn = getColumnLetter(startColumn + 2);
  lookupSheet.getCell(`${mappingColumn}1`).value = keyHeader;
  lookupSheet.getCell(`${rangeNameColumn}1`).value = `${keyHeader} Range`;
  lookupSheet.getCell(`${valuesColumn}1`).value = `${keyHeader} Values`;

  let mappingRow = 2;
  let valuesRow = 2;
  groups.forEach(({ key, values }) => {
    const normalizedValues = uniqueStrings(values);
    if (!key || normalizedValues.length === 0) {
      return;
    }

    const definedName = `${namePrefix}_${mappingRow}`;
    normalizedValues.forEach((value, index) => {
      lookupSheet.getCell(`${valuesColumn}${valuesRow + index}`).value = value;
    });
    const lastValueRow = valuesRow + normalizedValues.length - 1;
    workbook.definedNames.add(
      `Lookups!$${valuesColumn}$${valuesRow}:$${valuesColumn}$${lastValueRow}`,
      definedName,
    );
    lookupSheet.getCell(`${mappingColumn}${mappingRow}`).value = key;
    lookupSheet.getCell(`${rangeNameColumn}${mappingRow}`).value = definedName;
    mappingRow += 1;
    valuesRow = lastValueRow + 1;
  });

  return {
    mappingRange:
      mappingRow > 2
        ? `Lookups!$${mappingColumn}$2:$${rangeNameColumn}$${mappingRow - 1}`
        : null,
    nextColumn: startColumn + 3,
  };
};

const protectTemplate = async ({
  instructionSheet,
  lookupSheet,
  statusSheet,
  statusColumnCount,
  templateSheet,
  templateColumnCount,
}) => {
  for (let rowNumber = 2; rowNumber <= TEMPLATE_LAST_ROW; rowNumber += 1) {
    for (let columnNumber = 1; columnNumber <= templateColumnCount; columnNumber += 1) {
      templateSheet.getCell(rowNumber, columnNumber).protection = {
        locked: false,
      };
    }
  }
  for (let rowNumber = 2; rowNumber <= TEMPLATE_LAST_ROW; rowNumber += 1) {
    for (
      let columnNumber = 1;
      columnNumber <= statusColumnCount;
      columnNumber += 1
    ) {
      statusSheet.getCell(rowNumber, columnNumber).protection = {
        locked: false,
      };
    }
  }

  await Promise.all([
    templateSheet.protect(TEMPLATE_PASSWORD, {
      autoFilter: true,
      selectLockedCells: false,
      selectUnlockedCells: true,
      spinCount: 1000,
    }),
    statusSheet.protect(TEMPLATE_PASSWORD, {
      autoFilter: true,
      selectLockedCells: false,
      selectUnlockedCells: true,
      spinCount: 1000,
    }),
    instructionSheet.protect(TEMPLATE_PASSWORD, {
      selectLockedCells: true,
      selectUnlockedCells: true,
      spinCount: 1000,
    }),
    lookupSheet.protect(TEMPLATE_PASSWORD, {
      selectLockedCells: false,
      selectUnlockedCells: false,
      spinCount: 1000,
    }),
  ]);
};

export const buildStructuredTemplateWorkbookBuffer = async ({
  categoryLabel,
  requiresGpa = true,
  templateOptions,
}) => {
  const workbook = new ExcelJS.Workbook();
  const templateSheet = workbook.addWorksheet(`${categoryLabel} Template`);
  const statusSheet = workbook.addWorksheet(PRESERVICE_STATUS_SHEET_NAME);
  const instructionSheet = workbook.addWorksheet("Instructions");
  const lookupSheet = workbook.addWorksheet("Lookups");
  lookupSheet.state = "veryHidden";
  const currentYear = new Date().getFullYear();
  const maxBirthDate = `${currentYear - 17}-12-31`;
  const columnMap = templateOptions.headers.reduce((accumulator, header, index) => {
    accumulator[header] = getColumnLetter(index + 1);
    return accumulator;
  }, {});
  const lastColumnLetter = getColumnLetter(templateOptions.headers.length);
  const statusColumnMap = PRESERVICE_STATUS_TEMPLATE_HEADERS.reduce(
    (accumulator, header, index) => {
      accumulator[header] = getColumnLetter(index + 1);
      return accumulator;
    },
    {},
  );

  instructionSheet.addRow([
    `${categoryLabel} import template (version ${PRESERVICE_TEMPLATE_VERSION})`,
  ]);
  instructionSheet.addRow([
    `1. Enter student details in '${categoryLabel} Template'. Add GPA/status history in '${PRESERVICE_STATUS_SHEET_NAME}' when it is available.`,
  ]);
  instructionSheet.addRow([
    `2. Enter each student in '${categoryLabel} Template' first. In '${PRESERVICE_STATUS_SHEET_NAME}', select the National ID (FAN) from the dropdown linked to that student sheet.`,
  ]);
  instructionSheet.addRow([
    "3. Do not rename headers, add columns, or add sheets. Red headers are required; dark grey headers are optional.",
  ]);
  instructionSheet.addRow([
    "4. Use dropdown columns. Discipline depends on Program; Zone and Woreda depend on the selected parent location.",
  ]);
  instructionSheet.addRow([
    `5. Year of Entry must be between 1950 and ${currentYear}. Expected Graduation Year is required.`,
  ]);
  instructionSheet.addRow([
    "6. Expected Graduation Year and Year of Graduation may be in the future. Year of Graduation is optional, but if provided it cannot be earlier than Expected Graduation Year.",
  ]);
  instructionSheet.addRow([
    "7. National ID (FAN) must contain exactly 16 digits. Each National ID may have only one Student Status row for each Year of Education.",
  ]);
  instructionSheet.addRow([
    "8. GPA and Cumulative GPA are independent required values and must each be between 0 and 4. The system does not calculate Cumulative GPA.",
  ]);
  instructionSheet.addRow([
    `9. Nationality is required. Use '${templateOptions.nationalityDefault || "Ethiopia"}' unless another nationality applies.`,
  ]);
  if (columnMap["Date of Birth"]) {
    instructionSheet.addRow([
      `10. Date of Birth must be on or before ${maxBirthDate}.`,
    ]);
  }
  instructionSheet.getColumn(1).width = 120;

  templateSheet.addRow(templateOptions.headers);
  statusSheet.addRow(PRESERVICE_STATUS_TEMPLATE_HEADERS);
  templateSheet.views = [{ state: "frozen", ySplit: 1 }];
  statusSheet.views = [{ state: "frozen", ySplit: 1 }];
  templateSheet.autoFilter = {
    from: "A1",
    to: `${lastColumnLetter}1`,
  };
  statusSheet.autoFilter = {
    from: "A1",
    to: `${getColumnLetter(PRESERVICE_STATUS_TEMPLATE_HEADERS.length)}1`,
  };
  templateOptions.headers.forEach((header, index) => {
    const widerHeaders = new Set([
      "Background Profession",
      "Program Education Type",
      "Department / Discipline",
    ]);
    templateSheet.getColumn(index + 1).width = widerHeaders.has(header)
      ? 28
      : Math.max(14, Math.min(24, header.length + 4));
  });
  templateSheet.getColumn(columnMap["National ID (FAN)"]).numFmt = "@";
  PRESERVICE_STATUS_TEMPLATE_HEADERS.forEach((header, index) => {
    statusSheet.getColumn(index + 1).width =
      header === "National ID (FAN)" ? 24 : Math.max(16, header.length + 4);
  });
  statusSheet.getColumn(statusColumnMap["National ID (FAN)"]).numFmt = "@";
  workbook.definedNames.add(
    `'${templateSheet.name.replace(/'/g, "''")}'!$${columnMap["National ID (FAN)"]}$2:$${columnMap["National ID (FAN)"]}$${TEMPLATE_LAST_ROW}`,
    PROGRAM_FAN_RANGE_NAME,
  );

  const lookupGroups = [
    { header: "Sex", values: templateOptions.sexOptions },
    { header: "Ethnicity", values: templateOptions.ethnicityOptions },
    { header: "Birth Region", values: templateOptions.birthRegionOptions },
    { header: "Birth Zone", values: templateOptions.birthZoneOptions },
    { header: "Birth Woreda", values: templateOptions.birthWoredaOptions },
    {
      header: "Background Profession",
      values: templateOptions.backgroundProfessionOptions,
    },
    {
      header: "Higher Learning Institution",
      values: templateOptions.universityOptions,
    },
    {
      header: "Program Education Type",
      values:
        templateOptions.programEducationTypeOptions ||
        templateOptions.programOptions,
    },
    {
      header: "Department / Discipline",
      values: templateOptions.disciplineOptions,
    },
    {
      header: "Level Of Study",
      values: templateOptions.levelOfStudyOptions,
    },
    {
      header: "Year of Education",
      values: templateOptions.yearOfEducationOptions,
    },
    {
      header: "Student Status",
      values: templateOptions.studentStatusOptions,
    },
  ].filter(
    (group) =>
      (columnMap[group.header] || statusColumnMap[group.header]) &&
      Array.isArray(group.values) &&
      group.values.length > 0,
  );

  lookupSheet.addRow(lookupGroups.map((group) => group.header));
  const lookupHeight = Math.max(
    ...lookupGroups.map((group) => group.values.length),
  );
  for (let rowIndex = 0; rowIndex < lookupHeight; rowIndex += 1) {
    lookupSheet.addRow(
      lookupGroups.map((group) => group.values[rowIndex] || ""),
    );
  }

  const listFormulaByHeader = lookupGroups.reduce((accumulator, group, index) => {
    const columnLetter = getColumnLetter(index + 1);
    const rangeName = `PreserviceLookup_${index + 1}`;
    workbook.definedNames.add(
      `Lookups!$${columnLetter}$2:$${columnLetter}$${group.values.length + 1}`,
      rangeName,
    );
    accumulator[group.header] = rangeName;
    return accumulator;
  }, {});

  const birthHierarchy = templateOptions.birthHierarchy || {};
  const hierarchyRegions = birthHierarchy.regions || [];
  const hierarchyZones = birthHierarchy.zones || [];
  const hierarchyWoredas = birthHierarchy.woredas || [];
  const regionNameById = hierarchyRegions.reduce((result, region) => {
    if (region?.id != null && region?.name) {
      result[Number(region.id)] = String(region.name).trim();
    }
    return result;
  }, {});
  const zonesByRegionGroups = hierarchyRegions.map((region) => ({
    key: String(region?.name || "").trim(),
    values: hierarchyZones
      .filter((zone) => Number(zone?.regionId) === Number(region?.id))
      .map((zone) => zone?.name),
  }));
  const woredasByZoneGroups = hierarchyZones.map((zone) => ({
    key: `${regionNameById[Number(zone?.regionId)] || ""}|${String(
      zone?.name || "",
    ).trim()}`,
    values: hierarchyWoredas
      .filter((woreda) => Number(woreda?.zoneId) === Number(zone?.id))
      .map((woreda) => woreda?.name),
  }));
  const programHierarchy = templateOptions.programHierarchy || {};
  const hierarchyPrograms = programHierarchy.programs || [];
  const hierarchyDisciplines = programHierarchy.disciplines || [];
  const disciplinesByProgramGroups = hierarchyPrograms.map((program) => ({
    key: String(program?.name || "").trim(),
    values: hierarchyDisciplines
      .filter(
        (discipline) =>
          Number(discipline?.educationProgramId) === Number(program?.id),
      )
      .map((discipline) => discipline?.name),
  }));

  let dependentColumn = lookupGroups.length + 2;
  const regionZoneMap = addDependentListMap({
    groups: zonesByRegionGroups,
    lookupSheet,
    workbook,
    keyHeader: "Region to Zone",
    namePrefix: "PS_REGION",
    startColumn: dependentColumn,
  });
  dependentColumn = regionZoneMap.nextColumn + 1;
  const zoneWoredaMap = addDependentListMap({
    groups: woredasByZoneGroups,
    lookupSheet,
    workbook,
    keyHeader: "Region and Zone to Woreda",
    namePrefix: "PS_ZONE",
    startColumn: dependentColumn,
  });
  dependentColumn = zoneWoredaMap.nextColumn + 1;
  const programDisciplineMap = addDependentListMap({
    groups: disciplinesByProgramGroups,
    lookupSheet,
    workbook,
    keyHeader: "Program to Discipline",
    namePrefix: "PS_PROGRAM",
    startColumn: dependentColumn,
  });
  const emptyListColumn = getColumnLetter(programDisciplineMap.nextColumn + 1);
  lookupSheet.getCell(`${emptyListColumn}1`).value = "";
  workbook.definedNames.add(
    `Lookups!$${emptyListColumn}$1:$${emptyListColumn}$1`,
    "PreserviceEmptyList",
  );

  const setDependentValidation = ({
    childHeader,
    keyFormula,
    mappingRange,
    rowNumber,
  }) => {
    if (!columnMap[childHeader] || !mappingRange) {
      return false;
    }
    templateSheet.getCell(
      `${columnMap[childHeader]}${rowNumber}`,
    ).dataValidation = {
      type: "list",
      allowBlank: false,
      formulae: [
        `INDIRECT(IFERROR(VLOOKUP(${keyFormula},${mappingRange},2,FALSE),"PreserviceEmptyList"))`,
      ],
      showErrorMessage: true,
      errorTitle: `Invalid ${childHeader}`,
      error: `Select ${childHeader} after choosing its parent value.`,
    };
    return true;
  };

  for (let rowNumber = 2; rowNumber <= TEMPLATE_LAST_ROW; rowNumber += 1) {
    [
      "First Name",
      "Father Name",
      "Grand Father Name",
      "Nationality",
      "Town / Village",
    ].forEach((columnHeader) => {
      setRequiredTextValidation({
        columnHeader,
        columnMap,
        rowNumber,
        templateSheet,
      });
    });
    setListValidation({
      templateSheet,
      columnMap,
      listFormulaByHeader,
      header: "Sex",
      rowNumber,
      errorTitle: "Invalid Sex",
      error: "Select Male or Female from the dropdown.",
    });
    if (columnMap["Date of Birth"]) {
      templateSheet.getCell(
        `${columnMap["Date of Birth"]}${rowNumber}`,
      ).dataValidation = {
        type: "date",
        operator: "lessThanOrEqual",
        allowBlank: false,
        formulae: [new Date(`${maxBirthDate}T00:00:00`)],
        showErrorMessage: true,
        errorTitle: "Invalid Date of Birth",
        error: `Date of Birth must be on or before ${maxBirthDate}.`,
      };
    }
    templateSheet.getCell(
      `${columnMap["National ID (FAN)"]}${rowNumber}`,
    ).numFmt = "@";
    templateSheet.getCell(
      `${columnMap["National ID (FAN)"]}${rowNumber}`,
    ).dataValidation = {
      type: "textLength",
      operator: "equal",
      allowBlank: false,
      formulae: [16],
      showErrorMessage: true,
      errorTitle: "Invalid National ID",
      error:
        "National ID (FAN) must be exactly 16 digits and should be entered in a Text cell.",
    };
    setListValidation({
      templateSheet,
      columnMap,
      listFormulaByHeader,
      header: "Ethnicity",
      rowNumber,
      errorTitle: "Invalid Ethnicity",
      error: "Select an ethnicity from the dropdown list.",
    });
    setListValidation({
      templateSheet,
      columnMap,
      listFormulaByHeader,
      header: "Birth Region",
      rowNumber,
      errorTitle: "Invalid Birth Region",
      error: "Select a birth region from the dropdown list.",
    });
    const hasDependentZone = setDependentValidation({
      childHeader: "Birth Zone",
      keyFormula: `$${columnMap["Birth Region"]}${rowNumber}`,
      mappingRange: regionZoneMap.mappingRange,
      rowNumber,
    });
    if (!hasDependentZone) {
      setListValidation({
        templateSheet,
        columnMap,
        listFormulaByHeader,
        header: "Birth Zone",
        rowNumber,
        errorTitle: "Invalid Birth Zone",
        error: "Select a birth zone from the dropdown list.",
      });
    }
    const hasDependentWoreda = setDependentValidation({
      childHeader: "Birth Woreda",
      keyFormula: `$${columnMap["Birth Region"]}${rowNumber}&"|"&$${columnMap["Birth Zone"]}${rowNumber}`,
      mappingRange: zoneWoredaMap.mappingRange,
      rowNumber,
    });
    if (!hasDependentWoreda) {
      setListValidation({
        templateSheet,
        columnMap,
        listFormulaByHeader,
        header: "Birth Woreda",
        rowNumber,
        errorTitle: "Invalid Birth Woreda",
        error: "Select a birth woreda from the dropdown list.",
      });
    }
    setListValidation({
      templateSheet,
      columnMap,
      listFormulaByHeader,
      header: "Background Profession",
      rowNumber,
      errorTitle: "Invalid Background Profession",
      error: `Select one of: ${formatAllowedValues(templateOptions.backgroundProfessionOptions)}.`,
    });
    setListValidation({
      templateSheet,
      columnMap,
      listFormulaByHeader,
      header: "Higher Learning Institution",
      rowNumber,
      errorTitle: "Invalid Higher Learning Institution",
      error: "Select a Higher Learning Institution from the dropdown list.",
    });
    setListValidation({
      templateSheet,
      columnMap,
      listFormulaByHeader,
      header: "Program Education Type",
      rowNumber,
      errorTitle: "Invalid Program Education Type",
      error: `Select one of: ${formatAllowedValues(templateOptions.programEducationTypeOptions || templateOptions.programOptions)}.`,
    });
    const hasDependentDiscipline = setDependentValidation({
      childHeader: "Department / Discipline",
      keyFormula: `$${columnMap["Program Education Type"]}${rowNumber}`,
      mappingRange: programDisciplineMap.mappingRange,
      rowNumber,
    });
    if (!hasDependentDiscipline) {
      setListValidation({
        templateSheet,
        columnMap,
        listFormulaByHeader,
        header: "Department / Discipline",
        rowNumber,
        errorTitle: "Invalid Discipline",
        error: "Select a discipline from the dropdown list.",
      });
    }
    setListValidation({
      templateSheet,
      columnMap,
      listFormulaByHeader,
      header: "Level Of Study",
      rowNumber,
      errorTitle: "Invalid Level Of Study",
      error: "Select a level of study from the dropdown list.",
    });
    templateSheet.getCell(
      `${columnMap["Year of Entry"]}${rowNumber}`,
    ).dataValidation = {
      type: "whole",
      operator: "between",
      allowBlank: false,
      formulae: [1950, currentYear],
      showErrorMessage: true,
      errorTitle: "Invalid Entry Year",
      error: `Year of Entry must be between 1950 and ${currentYear}.`,
    };
    setYearValidation({
      templateSheet,
      columnMap,
      columnHeader: "Expected Graduation Year",
      rowNumber,
      allowBlank: false,
      errorTitle: "Invalid Expected Graduation Year",
      error: "Expected Graduation Year must be a valid 4-digit year.",
    });
    setYearValidation({
      templateSheet,
      columnMap,
      columnHeader: "Year of Graduation",
      rowNumber,
      allowBlank: true,
      errorTitle: "Invalid Graduation Year",
      error: "Year of Graduation must be a valid 4-digit year.",
    });
    setListValidation({
      templateSheet,
      columnMap,
      listFormulaByHeader,
      header: "Year of Education",
      rowNumber,
      errorTitle: "Invalid Year Of Education",
      error: "Select a Year of Education value from the dropdown list.",
    });
    setListValidation({
      templateSheet,
      columnMap,
      listFormulaByHeader,
      header: "Student Status",
      rowNumber,
      errorTitle: "Invalid Student Status",
      error: "Select a Student Status from the dropdown list.",
    });
    setGpaValidation({
      templateSheet,
      columnMap,
      columnHeader: "GPA",
      rowNumber,
      allowBlank: !requiresGpa,
      error: "GPA must be between 0 and 4.",
    });
    setGpaValidation({
      templateSheet,
      columnMap,
      columnHeader: "Cumulative GPA",
      rowNumber,
      allowBlank: !requiresGpa,
      error: "Cumulative GPA must be between 0 and 4.",
    });

    statusSheet.getCell(
      `${statusColumnMap["National ID (FAN)"]}${rowNumber}`,
    ).numFmt = "@";
    statusSheet.getCell(
      `${statusColumnMap["National ID (FAN)"]}${rowNumber}`,
    ).dataValidation = {
      type: "list",
      allowBlank: false,
      formulae: [PROGRAM_FAN_RANGE_NAME],
      errorStyle: "stop",
      showInputMessage: true,
      promptTitle: "Select Student FAN",
      prompt: "Select a FAN already entered in the student template sheet.",
      showErrorMessage: true,
      errorTitle: "FAN Not In Student Sheet",
      error:
        "Select a National ID (FAN) from the dropdown. Add the student to the program template sheet first.",
    };
    statusSheet.getCell(
      `${statusColumnMap["Year of Education"]}${rowNumber}`,
    ).dataValidation = {
      type: "list",
      allowBlank: false,
      formulae: [listFormulaByHeader["Year of Education"]],
      showErrorMessage: true,
      errorTitle: "Invalid Year Of Education",
      error: "Select a Year of Education value from the dropdown list.",
    };
    statusSheet.getCell(
      `${statusColumnMap["Student Status"]}${rowNumber}`,
    ).dataValidation = {
      type: "list",
      allowBlank: false,
      formulae: [listFormulaByHeader["Student Status"]],
      showErrorMessage: true,
      errorTitle: "Invalid Student Status",
      error: "Select a Student Status from the dropdown list.",
    };
    setGpaValidation({
      templateSheet: statusSheet,
      columnMap: statusColumnMap,
      columnHeader: "GPA",
      rowNumber,
      allowBlank: !requiresGpa,
      error: "GPA must be between 0 and 4.",
    });
    setGpaValidation({
      templateSheet: statusSheet,
      columnMap: statusColumnMap,
      columnHeader: "Cumulative GPA",
      rowNumber,
      allowBlank: false,
      error: "Cumulative GPA must be between 0 and 4.",
    });
  }

  styleRequiredHeaders({
    headers: templateOptions.headers,
    requiredHeaders: getRequiredPreserviceStudentHeaders(
      templateOptions.headers,
    ),
    sheet: templateSheet,
  });
  styleRequiredHeaders({
    headers: PRESERVICE_STATUS_TEMPLATE_HEADERS,
    requiredHeaders: PRESERVICE_STATUS_TEMPLATE_HEADERS,
    sheet: statusSheet,
  });
  statusSheet.getCell(
    1,
    PRESERVICE_STATUS_TEMPLATE_HEADERS.indexOf("National ID (FAN)") + 1,
  ).note =
    "Select this value from the dropdown. Its choices come from the National ID (FAN) column in the program template sheet.";
  templateSheet.getRow(1).height = 28;
  statusSheet.getRow(1).height = 28;

  await protectTemplate({
    instructionSheet,
    lookupSheet,
    statusSheet,
    statusColumnCount: PRESERVICE_STATUS_TEMPLATE_HEADERS.length,
    templateSheet,
    templateColumnCount: templateOptions.headers.length,
  });

  const workbookBuffer = await workbook.xlsx.writeBuffer();
  return addWorkbookStructureProtection(workbookBuffer);
};

export const downloadStructuredTemplateWorkbook = async (options) => {
  const protectedBuffer =
    await buildStructuredTemplateWorkbookBuffer(options);
  const blob = new Blob([protectedBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, options.fileName);
};
